import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { useQuery } from '@apollo/client/react';
import { useRouter } from 'next/router';
import { setCurrentItem, setError } from '../../../actions';
import WhiteHeader from '../../WhiteHeader';
import { getItemTitle } from '../../../utils/helpers/getCurrentItemDescriptionTitle';
import { selectRestoreAlertModalState } from '../../../store/selectors';
import useTogglePromotionModalView from '../../../hooks/useTogglePromotionModalView';
import { useCurrentItemData } from '../../../hooks/useCurrentItemData';
import { useSuccessPromotionAlertModal } from '../../../hooks/useSuccessPromotionAlertModal';
import iconSuccess from '../../../public/static/images/icon-payment-status-success.svg';
import useWebViewFailurePaymentAlertModal from '../../../hooks/useWebViewFailurePaymentAlertModal';
import useWebViewSuccessPaymentAlertModal from '../../../hooks/useWebViewSuccessPaymentAlertModal';
import { CurrentItemSections } from '../../Ad/Sections/CurrentItemSections';
import { CurrentItemPageSchema } from './CurrentItemPageSchema';
import { InformationPopup } from '../../ui/InformationPopup';
import { ButtonType } from '../../../styled/theme/types';
import useScreenSize from '../../../hooks/useScreenSize';
import { HeaderWrapper } from './styled';
import { SimilarAdsBlock } from '../../Ad/Sections/blocks/SimilarAdsBlock';
import { CurrentItemLayout } from '../../Ad/CurrentItemLayout';
import {
  CurrentItemQuery,
  ItemUpdateStatusEnum,
  UserRelatedItemQuery
} from '../../../graphql/graphql';
import { CurrentItemPlaceholder } from '../../Placeholders/CurrentItemPlaceholder';
import { useClient } from '../../../hooks/useClient';
import { getCurrentItem, getUserRelatedItem } from '../../../api/queries';
import { cleanHtmlExtension } from '../../../utils/helpers/middleware';
import Bugsnag from '../../../utils/bugsnag';
import { CurrentItemData } from '../../../utils/helpers/item';

const mapDispatchToProps = dispatch => {
  return {
    setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
  };
};

const CreditCalcModalView = dynamic(() => import('../../CreditCalcModalView'), {
  ssr: false
});

const PaymentStatusAlertModal = dynamic(
  () =>
    import('../../../bridge/components/PaymentStatusAlertModal').then(
      mod => mod.PaymentStatusAlertModal
    ),
  {
    ssr: false
  }
);

const ValidatedSection = dynamic(
  () =>
    import('../../Ad/Sections/ValidatedSection').then(
      mod => mod.ValidatedSection
    ),
  {
    ssr: false
  }
);

const CurrentItemPageComponent: React.FC<{
  setErrorUI: (value: boolean) => void;
  currentItemData: CurrentItemData;
  error: unknown | null;
  itemId: string;
  hasSecretKeyPresent: boolean;
  isSentHitEvent: boolean;
}> = ({
  hasSecretKeyPresent,
  setErrorUI,
  currentItemData,
  error,
  itemId,
  isSentHitEvent
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const router = useRouter();
  const { alert } = router.query;
  const alertParam = Array.isArray(alert) ? alert[0] : alert;
  const asPath = cleanHtmlExtension(router.asPath);
  const [isFavorited, setFavorited] = useState(false);
  const [isOpenCreditCalc, setOpenCreditCalc] = useState(false);
  const [isShowForbiddenAlert, setIsShowForbiddenAlert] = useState(false);
  const { isWebView } = useBridgeState();
  const { isSmall } = useScreenSize();
  const isClient = useClient();

  const { data: nonSsrCurrentItemData, loading: currentItemDataLoading } =
    useQuery(getCurrentItem, {
      variables: {
        id: itemId
      },
      ssr: false,
      skip: !!currentItemData
    });

  const {
    data: userRelatedItemQueryResponseData,
    loading: userRelatedItemLoading
  } = useQuery<UserRelatedItemQuery>(getUserRelatedItem, {
    variables: {
      id: itemId
    },
    ssr: false,
    skip: !currentItemData,
    fetchPolicy: 'cache-and-network'
  });

  const fullItemData = useMemo(() => {
    if (currentItemData) {
      return {
        ...currentItemData,
        ...userRelatedItemQueryResponseData?.item
      } as CurrentItemQuery['item'];
    }

    if (nonSsrCurrentItemData?.item) return nonSsrCurrentItemData.item;

    return null;
  }, [
    currentItemData,
    userRelatedItemQueryResponseData,
    nonSsrCurrentItemData
  ]);

  useEffect(() => {
    if (fullItemData) {
      dispatch(setCurrentItem(fullItemData));
    }
  }, [fullItemData]);

  useEffect(() => {
    if (!hasSecretKeyPresent) {
      Bugsnag.notify(new Error(`[MISSING ENV] VERCEL_SECRET_KEY is undefined`));
    }
  }, []);

  const { isVisible: isOpenRestoreAlertModal } = useSelector(
    selectRestoreAlertModalState
  );

  const {
    isOpenSuccessAlertModal,
    onAcceptSuccessPaymentModalTerms,
    successAlertModalIcon,
    successAlertModalTitle,
    successAlertModalMessage
  } = useSuccessPromotionAlertModal();

  const {
    isOpenWebViewSuccessAlertModal,
    successWebViewAlertModalTitle,
    successWebViewAlertModalMessage,
    onAcceptWebViewSuccessPaymentModal,
    payment
  } = useWebViewSuccessPaymentAlertModal();

  const {
    isOpenWebViewFailureAlertModal,
    onAcceptWebViewFailureHandler,
    failureWebViewAlertModalTitle,
    failureWebViewAlertModalMessage,
    failureWebViewAlertModalButtonTitle,
    failureWebViewAlertModalIcon
  } = useWebViewFailurePaymentAlertModal();

  const {
    item,
    maskedPhones,
    isEditAvailable,
    isRemoveAvailable,
    isAuthor,
    isResidence,
    isAgency,
    isPublished,
    isLimited,
    isExpired,
    isRejected,
    isPending,
    isExpiredManually,
    paidServices,
    itemPath,
    prettyPrice,
    prettyArea,
    prettyAreaWithUnits,
    prettyLandAreaWithUnits,
    showElectronicBanners,
    getPriceCurrency,
    isValidated,
    isLeased
  } = useCurrentItemData(fullItemData, asPath, isSentHitEvent);

  const {
    onOpenRestoreViewHandler,
    onOpenLimitPaymentViewHandler,
    onAcceptRestorePaymentHandleClick,
    onDeclineRestorePayment
  } = useTogglePromotionModalView(item || {});

  const itemTitleData = {
    lease: item?.leased,
    area: prettyArea,
    rooms: item?.rooms,
    areaCurrency: item?.area?.units,
    category: item?.category?.name,
    location: item?.location
  };

  const successMessage = successAlertModalMessage(item);

  const calcModalDescription = getItemTitle({
    ...itemTitleData,
    isClearString: true,
    simpleData: true
  });

  useEffect(() => {
    if (error) setErrorUI(true);
  }, [error]);

  useEffect(() => {
    if (!router.isReady) return;
    if (alertParam === ItemUpdateStatusEnum.Forbidden.toLowerCase()) {
      setIsShowForbiddenAlert(true);
    }
  }, [router.isReady, alertParam]);

  const handleCloseForbiddenPopup = () => {
    setIsShowForbiddenAlert(false);

    const { alert: _, ...rest } = router.query;
    router.replace({ pathname: router.pathname, query: rest }, undefined, {
      shallow: true
    });
  };

  if (isValidated) {
    return (
      <>
        {isSmall && isClient && (
          <HeaderWrapper $isWebView={isWebView}>
            <WhiteHeader
              {...{
                isFixedHeader: isWebView,
                isShowBackButton: true
              }}
            />
          </HeaderWrapper>
        )}
        <ValidatedSection item={item} />
      </>
    );
  }

  const addSchemaOrg = (isPublished || isExpired || isExpiredManually) && item;

  if (currentItemDataLoading) return <CurrentItemPlaceholder />;

  return (
    <>
      {addSchemaOrg && (
        <CurrentItemPageSchema
          {...{ item, isPublished, name: calcModalDescription }}
        />
      )}
      <CurrentItemLayout
        currentItem={item}
        isFavorited={isFavorited}
        setFavorited={setFavorited}
      >
        {isOpenCreditCalc && (
          <CreditCalcModalView
            summatyTitle={calcModalDescription}
            price={item?.price?.total}
            itemId={item?.id}
            initialBillOfSaleValue={item?.hasBillOfSale}
            {...{
              setOpenCreditCalc
            }}
          />
        )}

        <CurrentItemSections
          {...{
            data: fullItemData,
            item,
            maskedPhones,
            isEditAvailable,
            isRemoveAvailable,
            isAuthor,
            isResidence,
            isAgency,
            isPublished,
            isLimited,
            isExpired,
            isRejected,
            isPending,
            isLeased,
            isExpiredManually,
            paidServices,
            itemPath,
            prettyPrice,
            prettyArea,
            prettyAreaWithUnits,
            prettyLandAreaWithUnits,
            showElectronicBanners,
            getPriceCurrency,
            setOpenCreditCalc,
            onOpenRestoreViewHandler,
            onOpenLimitPaymentViewHandler,
            isFavorited,
            setFavorited,
            userRelatedItemLoading
          }}
        />

        <SimilarAdsBlock
          data={fullItemData}
          item={item}
          isAgency={isAgency}
          isResidence={isResidence}
        />
        <InformationPopup
          modalId="current-item-restore-modal"
          isOpen={isOpenRestoreAlertModal}
          title={t('pages.current_item.promotion.restore_alert_title')}
          message={t('pages.current_item.promotion.restore_alert_desc')}
          acceptButtonText={t('pages.current_item.buttons.restore')}
          declineButtonText={t('common.cancel')}
          onAcceptHandlerClick={onAcceptRestorePaymentHandleClick}
          onDeclineHandlerClick={onDeclineRestorePayment}
          acceptButtonState={ButtonType.default}
          declineButtonState={ButtonType.edit}
        />
        <InformationPopup
          icon={successAlertModalIcon}
          modalId="current-item-success-modal"
          isOpen={isOpenSuccessAlertModal}
          title={successAlertModalTitle}
          message={successMessage}
          declineButtonText={t('pages.current_item.buttons.accepted')}
          onDeclineHandlerClick={onAcceptSuccessPaymentModalTerms}
        />
        <InformationPopup
          modalId="current-item-forbidden-modal"
          isOpen={isShowForbiddenAlert}
          title={t('pages.current_item.attention')}
          message={t('pages.current_item.alert_modal.forbidden_message')}
          declineButtonText={t('pages.current_item.buttons.accepted')}
          onDeclineHandlerClick={handleCloseForbiddenPopup}
        />
        <PaymentStatusAlertModal
          isOpen={isOpenWebViewFailureAlertModal}
          icon={failureWebViewAlertModalIcon}
          title={failureWebViewAlertModalTitle}
          message={failureWebViewAlertModalMessage}
          buttonTitle={failureWebViewAlertModalButtonTitle}
          closeHandler={onAcceptWebViewFailureHandler}
          buttonColor="blue"
          isError
        />
        <PaymentStatusAlertModal
          isOpen={isOpenWebViewSuccessAlertModal}
          icon={iconSuccess}
          title={successWebViewAlertModalTitle}
          message={successWebViewAlertModalMessage}
          buttonTitle={t('webview.buttons.success')}
          closeHandler={onAcceptWebViewSuccessPaymentModal}
          payment={payment}
        />
      </CurrentItemLayout>
    </>
  );
};

export const CurrentItemPage = connect(
  null,
  mapDispatchToProps
)(CurrentItemPageComponent);
